import { useCallback, useEffect, useState } from "react";
import PermissionsViewer from "../../permission/components/PermissionsViewer";
import { Permission } from "../../permission/api";
import { usePermissions } from "../../permission/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { KeyboardArrowDown as ArrowDownIcon, KeyboardArrowUp as ArrowUpIcon } from "../../_app/components/icons";
import UILoader from "../../_app/components/UILoader";
import { createStylesheet } from "../../_app/utils/styles";
import { UIButton, UIGrid } from "../../_app/components";

interface Props {
  userId?: string;
  roleId?: string;
  levelId?: string;
  propagateData?: (perms: Permission[]) => void;
}

export const EditUserPermissionForm = ({ userId, roleId, levelId, propagateData = () => null }: Props) => {
  const classes = useStyles();
  const queryClient = useQueryClient();

  const [showPermissions, setShowPermissions] = useState(true);
  const togglePermissionsView = () => setShowPermissions(!showPermissions);

  const { data: permissionsList, isFetching } = usePermissions(userId, roleId, levelId);

  const [dirtyPerms, setDirtyPerms] = useState<Permission[]>([]);
  useEffect(() => {
    if (permissionsList) {
      setDirtyPerms([...permissionsList]);
    }
  }, [permissionsList]);

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["permissions", userId, roleId, levelId],
    });
  }, [queryClient, userId, roleId, levelId]);

  const onChange = (updated: any) => {
    setDirtyPerms(
      dirtyPerms?.map((p) => {
        if (p?.id === updated?.id) return updated;
        return p;
      }),
    );
  };

  const propagate = useCallback(
    (perms: Permission[]) => {
      propagateData(perms);
    },
    [propagateData],
  );

  useEffect(() => {
    propagate(dirtyPerms);
  }, [dirtyPerms, propagate]);

  return (
    <UIGrid container className={classes.ctr}>
      <UIGrid size={{ md: 4, sm: 1 }} />
      <UIGrid size={{ md: 8, sm: 12 }} className={classes.editPermsCtr}>
        <UIButton
          variant="contained"
          color="primary"
          size="small"
          onClick={togglePermissionsView}
          data-cy="edit-permissions"
          startIcon={showPermissions ? <ArrowUpIcon /> : <ArrowDownIcon />}
        >
          {showPermissions ? "Hide" : "Edit"} Individual Permissions
        </UIButton>
        {isFetching && (
          <div className={classes.loader}>
            <UILoader size={20} />
          </div>
        )}
      </UIGrid>
      {showPermissions && !isFetching && <PermissionsViewer permissions={dirtyPerms} onChange={onChange} />}
    </UIGrid>
  );
};

const useStyles = createStylesheet((theme) => ({
  ctr: {},
  loader: {
    marginLeft: "5px",
    marginTop: "5px",
  },
  editPermsCtr: {
    display: "flex",
    marginBottom: "2em",
    flexDirection: "row",
  },
}));

export default EditUserPermissionForm;
